import React, { useState, useEffect, useCallback } from "react";
import DynamicBanner from "../../../include/DynamicBanner";
import "./SportsCommon.css";
import api from "../../../../../api";
import { useLocation } from "react-router-dom";

const StudentSports = () => {
  const [activeTab, setActiveTab] = useState("files");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [tabs, setTabs] = useState([]);
  const [pageTitle, setPageTitle] = useState("");
  const [bannerData, setBannerData] = useState({
    title: "STUDENTS",
    images: [],
    mobileImages: []
  });

  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const slug = (() => {
    const parts = location.pathname.replace(/^\//, "").split("/");
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i]) return parts[i];
    }
    return "studentsports";
  })();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/api/sports-highlights/${slug}`);
        const data = res.data;

        setPageTitle(data.page_title || data.title || "STUDENTS");

        if (data.banner_data) {
          setBannerData({
            title: data.banner_data.title || data.page_title || "STUDENTS",
            images: Array.isArray(data.banner_data.images) ? data.banner_data.images : [],
            mobileImages: Array.isArray(data.banner_data.mobile_images) ? data.banner_data.mobile_images : []
          });
        } else {
          setBannerData({
            title: data.page_title || data.title || "STUDENTS",
            images: data.image_url ? [data.image_url] : []
          });
        }

        setTabs(data.tab_sections || []);

        if (data.tab_sections && data.tab_sections.length > 0) {
          const firstTab = data.tab_sections[0];
          // Check title if type is missing
          const tabType = firstTab.type || firstTab.title?.toLowerCase() || "files";
          setActiveTab(tabType);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching highlight page:", err);
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Helper to format URLs correctly
  const formatUrl = (url) => {
    if (!url) return "";
    const cleanUrl = url.startsWith("/") ? url : `/${url}`;
    return `${process.env.REACT_APP_API_URL}/public${cleanUrl}`;
  };

  // Build galleryItems
  const galleryItems = (() => {
    const galleryTab = tabs.find(t => t.type === "gallery" || t.title?.toLowerCase() === "gallery");
    if (!galleryTab) return [];
    return (galleryTab.items || []).map(it => ({
      src: formatUrl(it.url),
      caption: it.text || ""
    }));
  })();

  // Build filesItems - FIX: Checking title as well as type
  const filesItems = (() => {
    const filesTab = tabs.find(t => t.type === "files" || t.title?.toLowerCase() === "files");
    if (!filesTab) return [];
    return (filesTab.items || []).map(it => ({
      url: formatUrl(it.url),
      text: it.text || ""
    }));
  })();

  // Build videosItems
  const videosItems = (() => {
    const videoTab = tabs.find(
      t => t.type === "videos" || t.title?.toLowerCase() === "videos" || (t.items && t.items.some(i => i.type === "video"))
    );
    if (!videoTab) return [];
    return (videoTab.items || []).map(it => ({
      url: it.url,
      text: it.text
    }));
  })();

  // Lightbox functions
  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const showNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % galleryItems.length);
  }, [galleryItems.length]);

  const showPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  }, [galleryItems.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, showNext, showPrev]);

  if (loading) return null;

  return (
    <>
      <DynamicBanner
        title={bannerData.title || "SPORTS"}
        bgImage={bannerData.images}
        bgImageMobile={bannerData.mobileImages}
      />

      <section className="sports-common-section">
        <div className="container">
          <div className="sec-title text-center">
            <h2>{pageTitle || "STUDENTS"}</h2>
          </div>

          <div className="tabs-container mb-5">
            <div className="tab-nav">
              {tabs.map((tab, index) => {
                const tabType = tab.type || tab.title?.toLowerCase() || `tab-${index}`;
                return (
                  <button
                    key={index}
                    className={`tab-btn ${activeTab === tabType ? "active" : ""}`}
                    onClick={() => setActiveTab(tabType)}
                  >
                    {tabType.includes("files") && <i className="fa fa-files-o text-danger"></i>}
                    {tabType.includes("gallery") && <i className="fa fa-picture-o text-dark"></i>}
                    {tabType.includes("video") && <i className="fa fa-youtube-play text-danger"></i>}
                    &nbsp; {tab.title || tabType}
                  </button>
                );
              })}
            </div>

            <div className="tab-content-wrapper">
              {/* FILES */}
              {activeTab === "files" && (
                <div className="tab-content active">
                  <ul className="achievement-list">
                    {filesItems.length > 0 ? (
                      filesItems.map((f, idx) => (
                        <li key={idx}>
                          <a href={f.url} target="_blank" rel="noopener noreferrer">
                            <i className="link-icon fa fa-file-pdf-o"></i>
                            <span>{f.text}</span>
                          </a>
                        </li>
                      ))
                    ) : (
                      <p className="text-center">No files available.</p>
                    )}
                  </ul>
                </div>
              )}

              {/* GALLERY */}
              {activeTab === "gallery" && (
                <div className="tab-content active">
                  <div className="gallery-grid">
                    {galleryItems.map((item, index) => (
                      <div key={index} className="gallery-item" onClick={() => openLightbox(index)}>
                        <img src={item.src} alt={item.caption} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* VIDEOS */}
              {activeTab === "videos" && (
                <div className="tab-content active">
                  <div className="video-grid">
                    {videosItems.map((v, idx) => {
                      const embedUrl = v.url?.replace("watch?v=", "embed/");
                      return (
                        <div className="video-item" key={idx}>
                          <iframe src={embedUrl} allowFullScreen title={v.text}></iframe>
                          <p className="video-caption">{v.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* LIGHTBOX */}
        {lightboxOpen && galleryItems.length > 0 && (
          <div className="lightbox-modal active" onClick={closeLightbox}>
            <span className="lightbox-close">&times;</span>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={galleryItems[currentIndex].src} alt="" />
              <div className="lightbox-caption">{galleryItems[currentIndex].caption}</div>
            </div>
            <a className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); showPrev(); }}>&#10094;</a>
            <a className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); showNext(); }}>&#10095;</a>
          </div>
        )}
      </section>
    </>
  );
};

export default StudentSports;